import React, { useState } from 'react';
import Dropdown from '../../../../components/UI/Dropdown/Dropdown';
import Button from '../../../../components/UI/Button/Button';
import styles from './PurchaseFlatPageStageTwo.module.css';
import { AGREEMENT_PAGE } from '../../../../router/paths';
import { isValid, parse } from 'date-fns'
import cn from 'classnames';

type BuyPageStageTwoProps = {
    handleNextStage: () => void;
};

const BuyPageStageTwo: React.FC<BuyPageStageTwoProps> = ({
    handleNextStage,
}) => {
    const [errors, setErrors] = useState<{ [key: string]: boolean }>({});
    const [ownerType, setOwnerType] = useState('');
  
    const validate = (data: { [key: string]: string }) => {
        const newErrors: { [key: string]: boolean } = {};
    
        if (!data.ownerType || data.ownerType.trim() === '') {
            newErrors.ownerType = true;
        }
    
        if (!data.fullName || data.fullName.trim() === '') {
            newErrors.fullName = true;
        }
    
        if (!data.phoneNumber || data.phoneNumber.trim() === '' || data.phoneNumber.replace(/\D/g, '').length !== 11) {
            newErrors.phoneNumber = true;
        }
    
        return newErrors;
    };    
  
    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
  
      const form = e.target as HTMLFormElement;
      const formData = new FormData(form);
      formData.append('ownerType', ownerType);
  
      const data = Object.fromEntries(formData.entries()) as { [key: string]: string };
      const validationErrors = validate(data);
      if (!data.ownerType || !data.fullName || !data.phoneNumber) {
          validationErrors.general = true;
      }
  
      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
      } else {
        setErrors({});
        localStorage.setItem(
          'bookingLeadFlat',
          JSON.stringify({
            ownerType: ownerType === 'person' ? 'Физическое лицо' : 'Юридическое лицо',
            fullName: data.fullName,
            phoneNumber: data.phoneNumber,
          }),
        );
        handleNextStage();
      }
    };

    const formatPhoneNumber = (value: string) => {
      const digits = value.replace(/\D/g, '');
  
      if (digits === '' || digits === '+') {
        return '+7 ';
      }
  
      let formatted = '+7';
  
      if (digits.length > 1) {
        formatted += ` ${digits.slice(1, 4)}`;
      }
      if (digits.length > 4) {
        formatted += ` ${digits.slice(4, 7)}`;
      }
      if (digits.length > 7) {
        formatted += `-${digits.slice(7, 9)}`;
      }
      if (digits.length > 9) {
        formatted += `-${digits.slice(9, 11)}`;
      }
  
      return formatted;
    };
  
    const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const formattedValue = formatPhoneNumber(e.target.value);
      e.target.value = formattedValue;
    };
  
    const handlePhoneFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      if (!e.target.value.startsWith('+7')) {
        e.target.value = '+7';
      }
    };
  
    const handlePhoneBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      if (e.target.value === '+7 ' || e.target.value === '+7') {
        e.target.value = '';
      }
    };

    const handleDropdownChange = (value: string) => {
        setOwnerType(value);
        setErrors((prev) => ({ ...prev, ownerType: false, general: false }));
    };    
  
    const formatPassportNumber = (value: string) => {
        const digits = value.replace(/\D/g, '');
        
        if (digits.length <= 4) {
            return digits;
        }
        return `${digits.slice(0, 4)} ${digits.slice(4, 10)}`;
    };
  
    const handlePassportChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const formattedValue = formatPassportNumber(e.target.value);
        e.target.value = formattedValue;
    };
  
    const formatDate = (value: string) => {
        const digits = value.replace(/\D/g, '');

        if (digits.length <= 2) {
            return digits;
        }
        if (digits.length <= 4) {
            return `${digits.slice(0, 2)}.${digits.slice(2)}`;
        }
        return `${digits.slice(0, 2)}.${digits.slice(2, 4)}.${digits.slice(4, 8)}`;
    };
  
    const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const formattedValue = formatDate(e.target.value);
        e.target.value = formattedValue;

        if (formattedValue.length === 10) {
            if (!validateDate(formattedValue)) {
                e.target.setCustomValidity('Введите корректную дату');
                e.target.value = '';
            } else {
                e.target.setCustomValidity('');
            }
        }
    };
  
    const validateDate = (dateString: string): boolean => {
        const parsedDate = parse(dateString, 'dd.MM.yyyy', new Date());
        return isValid(parsedDate) && parsedDate <= new Date();
    };

    const getInputClassName = (fieldName: string) =>
        cn('text_subheaders_sh3', styles.form_field, errors[fieldName] ? styles.error_field : '');

    const handleAgreementClick = () => {
        window.open(AGREEMENT_PAGE, '_blank');
    };

    return (
        <form onSubmit={handleSubmit} className={styles.content}>
            <div className={styles.text_wrapper}>
                <h3>Основные параметры сделки</h3>
                <p className='text_body_b2'>Чтобы создать бронь, достаточно заполнить информацию о&nbsp;собственнике. Она нужна, чтобы заранее подготовить договор. Данные, указанные тут, не&nbsp;передаются третьим лицам и&nbsp;полностью защищены.</p>
            </div>
            <div className={styles.man_info_wrapper}>
                <Dropdown 
                    options={[
                        { value: 'person', label: 'Физическое лицо', description: 'Жена, муж, родители, дети' }, 
                        { value: 'company', label: 'Юридическое лицо', description: 'Организация, предприятие, фирма' }
                    ]} 
                    placeholder='Тип собственника'
                    onChange={handleDropdownChange} 
                    className={errors.ownerType ? styles.error_field : ''}
                />
                {errors.ownerType && (
                    <p className={cn('text_captions_c1', styles.error_text)}>Выберите тип собственника</p>
                )}
                <div className={styles.personal_info_wrapper}>
                    <div className={styles.personal_info_title}>
                        <p className={'text_subheaders_sh1'}>Персональная информация</p>
                    </div>
                    <div className={styles.personal_info_input_wrapper}>
                        <div>
                            <label htmlFor='fullName'>
                                <input
                                    className={getInputClassName('fullName')}
                                    name='fullName'
                                    id='fullName'
                                    type='text'
                                    placeholder='Имя и фамилия'
                                />
                            </label>
                        </div>
                        <div>
                            <label htmlFor='phoneNumber'>
                                <input
                                    className={getInputClassName('phoneNumber')}
                                    name='phoneNumber'
                                    id='phoneNumber'
                                    type='text'
                                    placeholder='Телефон'
                                    onFocus={handlePhoneFocus}
                                    onChange={handlePhoneChange}
                                    onBlur={handlePhoneBlur}
                                />
                            </label>
                        </div>
                    </div>
                </div>
            </div>
            <div className={styles.fixed_button_wrapper}>
                <div className={styles.fixed_button}>
                    <Button
                        colorType='blue'
                        roundType='small_rounded'
                        type='submit'
                        width='100%'
                    >
                        Продолжить
                    </Button>
                    <p className={'text_captions_c1'}>
                        Нажимая «Далее», вы <span className={styles.link} onClick={handleAgreementClick}>соглашаетесь на обработку персональных данных</span>.
                    </p>
                </div>
            </div>
        </form>
    );
};

export default BuyPageStageTwo;
